import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './index.scss';
import { getDateTimePartsFromIso } from '../../utils/helper';
import { SessionOptions } from '../Product/ProductSalesPrice/ProductSalesPrice';
import DOMPurify from 'dompurify';

export type Session = {
  sessionId: string;
  sessionName: string;
  startDate: string;
  endDate: string;
};

type InstructorLedSessionsProps = {
  sessions: SessionOptions[];
  title?: string;
  upcomingSessionsNote?: string;
  NoSessionsAvailableText: string;
  onSessionSelect?: (session: SessionOptions) => void;
};

const computeTilesPerPage = () => {
  if (typeof window === 'undefined') return 8;
  const el = document.querySelector('.ils-tiles-page') as HTMLElement | null;
  if (el) {
    const cols = window.getComputedStyle(el).getPropertyValue('--cols');
    const parsed = parseInt(cols, 10);
    if (!isNaN(parsed)) return parsed;
  }
  return 8;
};

const InstructorLedSessions = ({
  sessions,
  upcomingSessionsNote,
  NoSessionsAvailableText,
  onSessionSelect,
}: InstructorLedSessionsProps): React.JSX.Element => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedSession = sessions && sessions.length > 0 ? sessions[selectedIndex] : undefined;
  const [tilesPerPage, setTilesPerPage] = useState<number>(computeTilesPerPage());
  const [page, setPage] = useState<number>(0);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((sessions?.length || 0) / tilesPerPage));
  }, [sessions?.length, tilesPerPage]);

  useEffect(() => {
    const updateTiles = () => setTilesPerPage(computeTilesPerPage());
    updateTiles();
    window.addEventListener('resize', updateTiles);
    return () => window.removeEventListener('resize', updateTiles);
  }, []);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [totalPages, page]);

  useEffect(() => {
    if (!sessions || sessions.length === 0) {
      setSelectedIndex(0);
      setPage(0);
      return;
    }
    if (selectedIndex >= sessions.length) {
      setSelectedIndex(0);
      setPage(0);
    }
  }, [sessions, selectedIndex]);

  useEffect(() => {
    if (selectedSession) {
      onSessionSelect?.(selectedSession);
    }
  }, [selectedSession, onSessionSelect]);

  const pageStart = page * tilesPerPage;
  const pageItems = sessions ? sessions.slice(pageStart, pageStart + tilesPerPage) : [];

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const goPrev = () => {
    if (!canPrev) return;
    setPage((p) => Math.max(0, p - 1));
  };

  const goNext = () => {
    if (!canNext) return;
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    el.classList.remove('enter', 'enter-active');
    el.getBoundingClientRect();
    el.classList.add('enter');
    requestAnimationFrame(() => el.classList.add('enter-active'));
  }, [page, tilesPerPage]);

  const onKeyDownTiles = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  const hasArrows = Boolean(sessions && sessions.length > tilesPerPage);
  const tilesClass = `ils-tiles ${hasArrows ? 'with-arrows' : 'no-arrows'}`;

  return (
    <div className="instructorledsessions">
      <h3 className="ils-heading">Upcoming Sessions</h3>

      {sessions === null || sessions.length === 0 ? (
        <div className="no-session">{NoSessionsAvailableText}</div>
      ) : (
        <>
          {upcomingSessionsNote && <p>{upcomingSessionsNote}</p>}

          <div
            className={tilesClass}
            role="region"
            aria-label="Session list"
            onKeyDown={onKeyDownTiles}
          >
            {hasArrows && (
              <IconButton
                className="ils-nav left"
                onClick={goPrev}
                aria-label="Previous sessions"
                aria-disabled={!canPrev}
                tabIndex={0}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            <div className="ils-tiles-page enter enter-active" ref={pageRef}>
              {pageItems.map((s, idx) => {
                const globalIdx = pageStart + idx;
                const parts = getDateTimePartsFromIso(s.startDate);
                const isActive = globalIdx === selectedIndex;
                return (
                  <button
                    key={s.sessionId ?? s.title ?? `${globalIdx}`}
                    className={`ils-tile ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedIndex(globalIdx);
                      onSessionSelect?.(s);
                    }}
                    aria-pressed={isActive}
                    aria-label={`${parts.weekday}, ${parts.month} ${parts.dayNum}`}
                    type="button"
                  >
                    <span className="ils-tile-weekday">{parts.weekday}</span>
                    <span className="ils-tile-month">{parts.month.toUpperCase()}</span>
                    <span className="ils-tile-daynum">{parts.dayNum}</span>
                  </button>
                );
              })}
            </div>

            {hasArrows && (
              <IconButton
                className="ils-nav right"
                onClick={goNext}
                aria-label="Next sessions"
                aria-disabled={!canNext}
                tabIndex={0}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </div>

          <hr />

          {selectedSession && (
            <div className="ils-details" role="region" aria-label="Selected session details">
              <div className="ils-meta">
                <span className="label">Starts:</span>{' '}
                {getDateTimePartsFromIso(selectedSession.startDate)?.date}{' '}
                {selectedSession.timezone}
              </div>

              <div className="ils-meta">
                <span className="label">Ends:</span>{' '}
                {getDateTimePartsFromIso(selectedSession.endDate)?.date} {selectedSession.timezone}
              </div>

              {selectedSession.description && (
                <div className="ils-meta">
                  <span className="description label">Description:</span>
                  <span
                    className="ils-description"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedSession.description),
                    }}
                  />
                </div>
              )}

              <hr />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstructorLedSessions;
